import { useState } from 'react';
import type { UseFormSetError } from 'react-hook-form';
import { useAddDomain, useUpdateDomainCache } from '#api/domains.queries.ts';
import { handleServerFormError, ValidationError, type ErrorDetails, type RewordedErrorDetails } from '#api/errors.ts';
import { NEW_DOMAIN_FLAG, NO_DOMAIN_FLAG } from '../../SelectDomain.queries.ts';

/** The domain-select fields shared by the admin and endpoint identity provider forms */
type DomainFormFields = {
  domainID: string;
  domainName: string;
};

/**
 * This hook to create a new domain as part of oidc provider creation.  If a domain has already
 * been created, we'll return its id.  Otherwise, if we need to make a new domain, we'll fire the mutation and
 * return the new domain's ID.
 */
export function useCreatableDomain(formContext: { setError: UseFormSetError<DomainFormFields> }) {
  const [createdDomainId, setCreatedDomainId] = useState<string | undefined>();
  const addDomain = useAddDomain();
  const updateDomainCache = useUpdateDomainCache();
  const { setError } = formContext;

  const createOrUseExistingDomain = async (data: DomainFormFields) => {
    // We got partway through before, and we don't allow changing after that, so use the saved ID
    if (createdDomainId) {
      return createdDomainId;
    }

    if (data.domainID === NO_DOMAIN_FLAG) {
      return null;
    }

    if (data.domainID === NEW_DOMAIN_FLAG && data.domainName) {
      try {
        const newDomain = await addDomain.mutateAsync({
          domainName: data.domainName,
        });
        updateDomainCache(newDomain);
        setCreatedDomainId(newDomain.id);
        return newDomain.id;
      } catch (error) {
        if (error instanceof ValidationError) {
          handleServerFormError(error, makeMapDomainErrorsToForm(setError));
          return;
        }
        setError('root.global', { message: 'We couldn’t add the new domain, please try again' });
        return;
      }
    }

    return data.domainID;
  };

  return { createOrUseExistingDomain, hasCreatedDomain: Boolean(createdDomainId) };
}

function makeMapDomainErrorsToForm(setError: UseFormSetError<DomainFormFields>) {
  return function mapDomainErrorsToForm(err: ErrorDetails | RewordedErrorDetails) {
    const { message, path, code } = err;
    switch (path) {
      case 'domainName':
        if (code === 'ERR_DUPLICATE_VALUE')
          setError(
            'domainName',
            { message: 'This domain already exists.  Choose it from the dropdown above.' },
            { shouldFocus: true }
          );
        return true;
      case 'root.global':
      case undefined:
        setError('root.global', { message });
        return true;
      default:
        setError('root.global', { message });
        return false;
    }
  };
}
